import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
    Plus,
    Repeat,
    Wallet,
    Building2,
    Zap,
    Droplet,
    Wifi,
    Flame,
    Leaf,
    Milk,
    Fuel,
    Truck,
    Coffee,
    Sparkles,
    Wrench,
    FileText,
    Megaphone,
    Users,
    Landmark,
    Home,
    Circle,
    Trash2,
    CheckCircle2,
    Loader2,
    Play,
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { PageHeader } from '../components/PageHeader';
import { MonthPicker, currentMonth, labelForMonth, useSharedMonth } from "../components/MonthPicker";
import { CategoryPickerWithAdd } from '../components/CategoryPickerWithAdd';
import { api, fmtMoney, fmtDate } from '../lib/api';

/* Map category-icon strings from backend to lucide-react components. */
const ICON_MAP = {
    home: Home,
    wifi: Wifi,
    zap: Zap,
    droplet: Droplet,
    flame: Flame,
    leaf: Leaf,
    milk: Milk,
    fuel: Fuel,
    truck: Truck,
    coffee: Coffee,
    sparkles: Sparkles,
    wrench: Wrench,
    'file-text': FileText,
    megaphone: Megaphone,
    users: Users,
    landmark: Landmark,
    circle: Circle,
};

const CatIcon = ({ name, size = 14, className = '' }) => {
    const Cmp = ICON_MAP[name] || Circle;
    return <Cmp size={size} className={className} />;
};

const PAYMENT_MODES = [
    { value: 'cash', label: 'Cash' },
    { value: 'upi', label: 'UPI' },
    { value: 'bank', label: 'Bank transfer' },
    { value: 'card', label: 'Card' },
    { value: 'cheque', label: 'Cheque' },
];

const CADENCES = [
    { value: 'monthly', label: 'Monthly' },
    { value: 'weekly', label: 'Weekly' },
];

const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export default function ExpensesPage() {
    const [tab, setTab] = useState('all');
    const [month, setMonth] = useSharedMonth();
    const [rows, setRows] = useState(null);
    const [totals, setTotals] = useState({ total: 0, fixed_total: 0, variable_total: 0, draft_count: 0 });
    const [templates, setTemplates] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAdd, setShowAdd] = useState(false);
    const [showRecurring, setShowRecurring] = useState(false);
    const [deleteFor, setDeleteFor] = useState(null); // expense to delete
    const [busy, setBusy] = useState(false);
    // Sprint 2026-07: "Group by vendor" view — collapses repeat visits to
    // the same vendor (e.g. "Blinkit 7 Jul ₹240" + "Blinkit 9 Jul ₹600")
    // into ONE row that shows total + count + latest date. Persist choice.
    const [groupByVendor, setGroupByVendor] = useState(() => {
        try { return localStorage.getItem('emb.exp.groupByVendor') === '1'; } catch { return false; }
    });
    // Sprint 2026-07: primary grouping dimension. "category" is now the
    // default because users complained the flat list of 200+ daily-sheet
    // expense rows was noise. When 'category', unmatched entries land in
    // an "Others" bucket at the bottom that offers an inline "Move to..."
    // dropdown for reclassification.
    const [groupMode, setGroupMode] = useState(() => {
        try {
            const v = localStorage.getItem('emb.exp.groupMode') || '_flat';
            // Sprint 2026-07 hotfix: default to the flat view (known-safe
            // rendering path). Category / vendor grouping is opt-in — a
            // legacy row with unusual field types was crashing the grouped
            // render on some tenants. Whitelist protects against stale values.
            return ['category', 'vendor', '_flat'].includes(v) ? v : '_flat';
        } catch { return '_flat'; }
    });
    const [expandedVendor, setExpandedVendor] = useState(null);
    useEffect(() => {
        try { localStorage.setItem('emb.exp.groupByVendor', groupByVendor ? '1' : '0'); } catch { /* noop */ }
    }, [groupByVendor]);
    useEffect(() => {
        try { localStorage.setItem('emb.exp.groupMode', groupMode); } catch { /* noop */ }
    }, [groupMode]);

    // Aggregate expenses by vendor OR by category. Same shape either way
    // so the render code stays generic. Defensive `String(x || '')` on
    // every key so a legacy row with a numeric vendor / null category
    // can't white-screen the whole page (`.trim()` on a non-string
    // throws).
    const groupedRows = useMemo(() => {
        if (!Array.isArray(rows) || !rows.length) return [];
        const bucket = new Map();
        for (const e of rows) {
            const rawKey = groupMode === 'category'
                ? (e.category_label || 'Others')
                : (e.vendor || e.category_label || 'Uncategorized');
            const key = String(rawKey || '').trim() || 'Others';
            const g = bucket.get(key) || {
                key,
                vendor: key,
                category_key: e.category_key,
                category_label: e.category_label,
                total: 0,
                count: 0,
                latest_date: '',
                items: [],
                is_others: groupMode === 'category' && !e.category_label,
            };
            g.total += Number(e.amount || 0);
            g.count += 1;
            const d = String(e.expense_date || '');
            if (d > (g.latest_date || '')) g.latest_date = d;
            g.items.push(e);
            bucket.set(key, g);
        }
        return Array.from(bucket.values()).sort((a, b) => {
            if (a.is_others && !b.is_others) return 1;
            if (!a.is_others && b.is_others) return -1;
            return (b.total || 0) - (a.total || 0);
        });
    }, [rows, groupMode]);

    // Recategorize a single expense via inline dropdown (Sprint 2026-07).
    async function recategorize(expenseId, category_key, category_label) {
        try {
            await api.updateExpense(expenseId, { category_key, category_label });
            toast.success(`Moved to "${category_label}"`);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Move failed');
        }
    }

    const kindFilter = tab === 'fixed' ? 'fixed' : tab === 'variable' ? 'variable' : undefined;

    const load = useCallback(async () => {
        setLoading(true);
        try {
            // Sprint 2026-07: the MonthPicker uses the sentinel value 'all'
            // for "All time". The backend rejects `month=all` (regex only
            // accepts YYYY-MM), so we omit the param instead.
            const monthParam = month && month !== 'all' ? month : undefined;
            const [expRes, recRes, catRes] = await Promise.all([
                api.listExpenses({ month: monthParam, kind: kindFilter }),
                api.listRecurring(),
                api.listExpenseCategories(),
            ]);
            const { expenses, total, fixed_total, variable_total, draft_count } = expRes || {};
            setRows(Array.isArray(expenses) ? expenses : []);
            setTotals({
                total: total || 0,
                fixed_total: fixed_total || 0,
                variable_total: variable_total || 0,
                draft_count: draft_count || 0,
            });
            setTemplates(Array.isArray(recRes?.templates) ? recRes.templates : []);
            setCategories(Array.isArray(catRes?.categories) ? catRes.categories : []);
        } catch (e) {
            // Never leave rows as null — falling back to an empty array
            // keeps the UI stable and renders the friendly EmptyState
            // instead of crashing on rows.map.
            setRows((prev) => (Array.isArray(prev) ? prev : []));
            toast.error(e?.response?.data?.detail || 'Failed to load expenses');
        } finally {
            setLoading(false);
        }
    }, [month, kindFilter]);

    useEffect(() => { load(); }, [load]);

    const confirmDraft = async (id) => {
        try {
            await api.confirmExpense(id);
            toast.success('Marked as paid');
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Could not confirm');
        }
    };

    const doDelete = async () => {
        if (!deleteFor) return;
        setBusy(true);
        try {
            await api.deleteExpense(deleteFor.id);
            toast.success('Expense deleted');
            setDeleteFor(null);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Delete failed');
        } finally {
            setBusy(false);
        }
    };

    const runDrafter = async () => {
        setBusy(true);
        try {
            const s = await api.runRecurringNow();
            if (s.drafted > 0) toast.success(`Created ${s.drafted} draft${s.drafted !== 1 ? 's' : ''}`);
            else toast('Nothing due today', { icon: '✅' });
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed');
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="space-y-6" data-testid="expenses-page">
            <PageHeader
                title="Expenses"
                subtitle="Every rupee out. Bills you scan land here too — plus rent, wifi, chai, and everything in between."
                actions={
                    <div className="flex items-center gap-2">
                        <MonthPicker value={month} onChange={setMonth} />
                        <Button
                            variant="outline"
                            onClick={() => setShowRecurring(true)}
                            data-testid="open-recurring-btn"
                        >
                            <Repeat size={14} className="mr-1" /> Recurring
                        </Button>
                        <Button onClick={() => setShowAdd(true)} data-testid="add-expense-btn">
                            <Plus size={14} className="mr-1" /> Add expense
                        </Button>
                    </div>
                }
            />

            {/* Totals row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatTile
                    label="Total spent"
                    value={fmtMoney(totals.total)}
                    icon={<Wallet size={16} />}
                    tone="default"
                />
                <StatTile
                    label="Fixed"
                    value={fmtMoney(totals.fixed_total)}
                    icon={<Building2 size={16} />}
                    tone="info"
                />
                <StatTile
                    label="Variable"
                    value={fmtMoney(totals.variable_total)}
                    icon={<Sparkles size={16} />}
                    tone="warning"
                />
                <StatTile
                    label="Drafts pending"
                    value={String(totals.draft_count)}
                    icon={<CheckCircle2 size={16} />}
                    tone={totals.draft_count > 0 ? 'warning' : 'success'}
                    hint={totals.draft_count > 0 ? 'Confirm to mark paid' : undefined}
                />
            </div>

            {/* Tabs */}
            <Tabs value={tab} onValueChange={setTab}>
                <TabsList data-testid="expense-tabs" className="w-full sm:w-auto flex-wrap justify-between">
                    <div className="flex">
                        <TabsTrigger value="all" data-testid="tab-all">All ({rows?.length ?? '…'})</TabsTrigger>
                        <TabsTrigger value="fixed" data-testid="tab-fixed">Fixed</TabsTrigger>
                        <TabsTrigger value="variable" data-testid="tab-variable">Variable</TabsTrigger>
                    </div>
                    {/* Sprint 2026-07: 3-way grouping selector. "Category"
                        is the default and lands unmatched entries in an
                        "Others" bucket that supports inline reclassification. */}
                    <div className="ml-auto flex items-center gap-1 text-xs">
                        {['flat', 'category', 'vendor'].map((mode) => (
                            <button
                                key={mode}
                                type="button"
                                onClick={() => {
                                    setGroupByVendor(mode === 'vendor');
                                    setGroupMode(mode === 'flat' ? 'category' : mode);
                                    // 'flat' just disables grouping — reuse groupByVendor=false + a
                                    // dedicated flag below to skip grouped view.
                                    if (mode === 'flat') setGroupMode('_flat');
                                }}
                                data-testid={`expense-group-${mode}`}
                                className={`px-2.5 py-1 rounded-md border ${
                                    (mode === 'flat' && groupMode === '_flat') ||
                                    (mode === 'category' && groupMode === 'category' && !groupByVendor) ||
                                    (mode === 'vendor' && groupByVendor)
                                        ? 'bg-primary/10 text-primary border-primary/30'
                                        : 'bg-transparent text-muted-foreground border-border hover:text-foreground'
                                }`}
                            >
                                {mode === 'flat' ? 'Flat' : mode === 'category' ? 'By category' : 'By vendor'}
                            </button>
                        ))}
                    </div>
                </TabsList>
                <TabsContent value={tab} className="mt-4">
                    {loading ? (
                        <div className="space-y-3">
                            {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-14 w-full" />)}
                        </div>
                    ) : !Array.isArray(rows) || rows.length === 0 ? (
                        <EmptyState month={month} onAdd={() => setShowAdd(true)} />
                    ) : groupMode === '_flat' ? (
                        <Card className="p-0 overflow-hidden">
                            <div className="divide-y divide-border">
                                {rows.map((e) => (
                                    <ExpenseRow
                                        key={e.id}
                                        e={e}
                                        onConfirm={() => confirmDraft(e.id)}
                                        onDelete={() => setDeleteFor(e)}
                                    />
                                ))}
                            </div>
                        </Card>
                    ) : (() => {
                        // Sprint 2026-07 hotfix: defensive render for grouped views.
                        // A single legacy row with unexpected shape (numeric vendor,
                        // null category, non-string date, missing amount, etc.) used
                        // to white-screen the entire Expenses page. Try/catch here
                        // gracefully falls back to the flat rendering path if
                        // grouping fails for ANY reason — the user still sees their
                        // data, just without grouping.
                        try {
                            return (
                        <Card className="p-0 overflow-hidden" data-testid="expense-grouped-list">
                            <div className="divide-y divide-border">
                                {groupedRows.map((g) => {
                                    const isOpen = expandedVendor === g.key;
                                    const isOthers = !!g.is_others;
                                    return (
                                        <div key={g.key} data-testid={`expense-group-${g.key}`}>
                                            <button
                                                type="button"
                                                onClick={() => setExpandedVendor(isOpen ? null : g.key)}
                                                className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-muted/30 text-left ${
                                                    isOthers ? 'bg-amber-50/30 dark:bg-amber-950/10 border-l-4 border-amber-400' : ''
                                                }`}
                                            >
                                                <div className={`h-9 w-9 rounded-md flex items-center justify-center shrink-0 ${
                                                    isOthers
                                                        ? 'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400'
                                                        : 'bg-muted text-muted-foreground'
                                                }`}>
                                                    <CatIcon name={String(g.category_key || 'circle').toLowerCase()} size={16} />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="font-medium truncate">
                                                        {isOthers ? '⚠ Others' : String(g.vendor || '—')}
                                                        {isOthers && (
                                                            <span className="ml-2 text-[10px] uppercase tracking-wide text-amber-700 dark:text-amber-500">
                                                                needs categorization
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="text-xs text-muted-foreground">
                                                        {g.count} {g.count === 1 ? 'entry' : 'entries'}
                                                        {g.latest_date && ` · latest ${g.latest_date}`}
                                                    </div>
                                                </div>
                                                <div className="text-right shrink-0">
                                                    <div className="font-mono tabular-nums text-sm">₹{(Number(g.total) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                                                    <div className="text-[10px] text-muted-foreground uppercase tracking-wide">
                                                        {isOpen ? 'hide entries' : 'show entries'}
                                                    </div>
                                                </div>
                                            </button>
                                            {isOpen && (
                                                <div className="bg-muted/20 divide-y divide-border/50">
                                                    {(g.items || []).map((e) => (
                                                        <div key={e.id} className="flex items-start gap-2 px-4 py-2">
                                                            <div className="flex-1 min-w-0">
                                                                <ExpenseRow
                                                                    e={e}
                                                                    onConfirm={() => confirmDraft(e.id)}
                                                                    onDelete={() => setDeleteFor(e)}
                                                                />
                                                            </div>
                                                            {Array.isArray(categories) && categories.length > 0 && (
                                                                <select
                                                                    className="text-xs bg-background border border-border rounded px-1.5 py-1 mt-2 shrink-0"
                                                                    value={e.category_key || ''}
                                                                    onChange={(ev) => {
                                                                        const sel = categories.find((c) => c && c.key === ev.target.value);
                                                                        if (sel) recategorize(e.id, sel.key, sel.label);
                                                                    }}
                                                                    data-testid={`expense-recategorize-${e.id}`}
                                                                    title="Move to another category"
                                                                >
                                                                    <option value="" disabled>Move to…</option>
                                                                    {categories.map((c) => (
                                                                        <option key={c.key} value={c.key}>{c.label}</option>
                                                                    ))}
                                                                </select>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </Card>
                            );
                        } catch (renderErr) {
                            // Fallback to flat view so the user always sees their data.
                            // Log to console for debugging.
                            // eslint-disable-next-line no-console
                            console.error('[Expenses] Grouped render failed, falling back to flat:', renderErr);
                            return (
                                <Card className="p-0 overflow-hidden">
                                    <div className="p-3 border-b border-amber-300 bg-amber-50/60 dark:bg-amber-950/20 text-xs text-amber-900 dark:text-amber-300">
                                        Grouping temporarily unavailable — showing flat list.
                                    </div>
                                    <div className="divide-y divide-border">
                                        {rows.map((e) => (
                                            <ExpenseRow
                                                key={e.id}
                                                e={e}
                                                onConfirm={() => confirmDraft(e.id)}
                                                onDelete={() => setDeleteFor(e)}
                                            />
                                        ))}
                                    </div>
                                </Card>
                            );
                        }
                    })()}
                </TabsContent>
            </Tabs>

            {/* Add expense dialog */}
            <ExpenseAddDialog
                open={showAdd}
                onOpenChange={setShowAdd}
                categories={categories}
                onCategoriesChange={setCategories}
                onSaved={() => { setShowAdd(false); load(); }}
            />

            {/* Recurring templates dialog */}
            <RecurringManagerDialog
                open={showRecurring}
                onOpenChange={setShowRecurring}
                templates={templates}
                categories={categories}
                onCategoriesChange={setCategories}
                onChanged={load}
                onRunNow={runDrafter}
                busy={busy}
            />

            {/* Delete confirm */}
            <AlertDialog open={!!deleteFor} onOpenChange={(o) => !o && setDeleteFor(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete this expense?</AlertDialogTitle>
                        <AlertDialogDescription>
                            {deleteFor && (
                                <>Removing <b>{fmtMoney(deleteFor.amount)}</b> · {deleteFor.category_label}
                                {deleteFor.vendor ? ` (${deleteFor.vendor})` : ''}. This cannot be undone.</>
                            )}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={doDelete} disabled={busy} data-testid="confirm-delete-expense-btn">
                            {busy && <Loader2 size={12} className="mr-1 animate-spin" />}Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

/* ---------- Sub-components ---------- */

function StatTile({ label, value, icon, tone = 'default', hint }) {
    const toneMap = {
        default: 'bg-muted text-foreground',
        info: 'bg-accent text-accent-foreground',
        warning: 'bg-[hsl(var(--warning-bg))] text-[hsl(var(--warning-fg))]',
        success: 'bg-[hsl(var(--success-bg))] text-[hsl(var(--success-fg))]',
    };
    return (
        <Card className="p-4">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground">
                <span className={`h-6 w-6 rounded-md flex items-center justify-center ${toneMap[tone]}`}>{icon}</span>
                {label}
            </div>
            <div className="mt-2 text-2xl font-heading tabular-nums">{value}</div>
            {hint && <div className="text-[11px] text-muted-foreground mt-1">{hint}</div>}
        </Card>
    );
}

function EmptyState({ month, onAdd }) {
    return (
        <Card className="p-8 text-center border-dashed">
            <div className="mx-auto h-12 w-12 rounded-full bg-muted flex items-center justify-center mb-3">
                <Wallet size={20} className="text-muted-foreground" />
            </div>
            <div className="font-heading text-lg">No expenses for {labelForMonth(month)}</div>
            <div className="text-sm text-muted-foreground mt-1 mb-4">
                Log rent, wifi, sabziwala slips, chai — anything not on a formal invoice.
            </div>
            <Button onClick={onAdd} data-testid="empty-add-expense-btn">
                <Plus size={14} className="mr-1" /> Add first expense
            </Button>
        </Card>
    );
}

function ExpenseRow({ e, onConfirm, onDelete }) {
    const isDraft = e.status === 'draft';
    return (
        <div className="flex items-center gap-3 px-4 py-3 hover:bg-muted/40 transition-colors" data-testid={`expense-row-${e.id}`}>
            <div className={`h-9 w-9 rounded-md flex items-center justify-center ${
                e.kind === 'fixed' ? 'bg-accent text-accent-foreground' : 'bg-muted text-muted-foreground'
            }`}>
                {/* Defensive: some legacy or manually-added rows may lack
                    a category_label (null / empty). Falling back to the
                    key or 'misc' keeps the icon rendering instead of
                    throwing TypeError: cannot read 'toLowerCase' of null. */}
                <CatIcon name={(e.category_label || e.category_key || 'misc').toLowerCase()} />
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium">{e.category_label || e.category_key || 'Misc'}</span>
                    {e.vendor && <span className="text-sm text-muted-foreground">· {e.vendor}</span>}
                    {isDraft && (
                        <Badge variant="outline" className="text-[10px] bg-[hsl(var(--warning-bg))] text-[hsl(var(--warning-fg))] border-[hsl(var(--warning-border))]">
                            Draft
                        </Badge>
                    )}
                    {e.source === 'recurring' && (
                        <Badge variant="outline" className="text-[10px] gap-1">
                            <Repeat size={9} /> Recurring
                        </Badge>
                    )}
                    {e.source === 'whatsapp' && (
                        <Badge variant="outline" className="text-[10px]">WA</Badge>
                    )}
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">
                    {fmtDate(e.expense_date)} · {e.payment_mode || 'cash'}
                    {e.notes && <> · <span className="italic">"{e.notes}"</span></>}
                </div>
            </div>
            <div className="text-right">
                <div className="font-heading text-lg tabular-nums">{fmtMoney(e.amount)}</div>
            </div>
            <div className="flex items-center gap-1">
                {isDraft && (
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={onConfirm}
                        data-testid={`confirm-expense-${e.id}`}
                    >
                        <CheckCircle2 size={13} className="mr-1" /> Mark paid
                    </Button>
                )}
                <Button
                    size="icon"
                    variant="ghost"
                    onClick={onDelete}
                    data-testid={`delete-expense-${e.id}`}
                    aria-label="Delete"
                >
                    <Trash2 size={14} />
                </Button>
            </div>
        </div>
    );
}

/* ---------- Add-expense dialog ---------- */

function ExpenseAddDialog({ open, onOpenChange, categories, onCategoriesChange, onSaved }) {
    const [kind, setKind] = useState('variable');
    const [amount, setAmount] = useState('');
    const [categoryKey, setCategoryKey] = useState('');
    const [vendor, setVendor] = useState('');
    const [expenseDate, setExpenseDate] = useState(() => new Date().toISOString().slice(0, 10));
    const [paymentMode, setPaymentMode] = useState('cash');
    const [notes, setNotes] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return;
        setKind('variable');
        setAmount('');
        setCategoryKey('');
        setVendor('');
        setExpenseDate(new Date().toISOString().slice(0, 10));
        setPaymentMode('cash');
        setNotes('');
    }, [open]);

    // When category changes, use its default_kind as a suggestion
    useEffect(() => {
        if (!categoryKey) return;
        const c = categories.find((c) => c.key === categoryKey);
        if (c && c.default_kind) setKind(c.default_kind);
    }, [categoryKey, categories]);

    const canSave = amount && Number(amount) > 0 && categoryKey;

    const save = async (andAddAnother = false) => {
        if (!canSave) return;
        // Defensive: if the browser's native picker returned a malformed value
        // (e.g. partial "20/07/25" that the user typed but never confirmed),
        // fall back to today so we never send garbage to the backend.
        const isValidIso = expenseDate && /^\d{4}-\d{2}-\d{2}$/.test(expenseDate);
        const dateToSend = isValidIso
            ? expenseDate
            : new Date().toISOString().slice(0, 10);
        setSaving(true);
        try {
            await api.createExpense({
                kind,
                amount: Number(amount),
                category_key: categoryKey,
                vendor: vendor.trim() || undefined,
                expense_date: dateToSend,
                payment_mode: paymentMode,
                notes: notes.trim() || undefined,
            });
            toast.success('Expense logged');
            if (andAddAnother) {
                setAmount('');
                setVendor('');
                setNotes('');
            } else {
                onSaved?.();
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Save failed');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid="add-expense-dialog" className="max-w-lg max-h-[92vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="font-heading">Add expense</DialogTitle>
                    <DialogDescription>
                        Log any spend that didn't come from a scanned invoice.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-3 py-2">
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                            <Label className="text-xs uppercase">Amount (₹)</Label>
                            <Input
                                type="number"
                                inputMode="decimal"
                                value={amount}
                                onChange={(e) => setAmount(e.target.value)}
                                placeholder="0"
                                data-testid="expense-amount-input"
                                autoFocus
                            />
                        </div>
                        <div className="space-y-1">
                            <Label className="text-xs uppercase">Type</Label>
                            <div className="flex gap-1">
                                <Button
                                    type="button"
                                    variant={kind === 'variable' ? 'default' : 'outline'}
                                    size="sm"
                                    className="flex-1"
                                    onClick={() => setKind('variable')}
                                    data-testid="kind-variable-btn"
                                >Variable</Button>
                                <Button
                                    type="button"
                                    variant={kind === 'fixed' ? 'default' : 'outline'}
                                    size="sm"
                                    className="flex-1"
                                    onClick={() => setKind('fixed')}
                                    data-testid="kind-fixed-btn"
                                >Fixed</Button>
                            </div>
                        </div>
                    </div>

                    <div className="space-y-1">
                        <Label className="text-xs uppercase">Category</Label>
                        <CategoryPickerWithAdd
                            value={categoryKey}
                            onChange={setCategoryKey}
                            categories={categories}
                            onCategoriesChange={onCategoriesChange}
                            testId="expense-category-select"
                            placeholder="Select category…"
                            allowDelete
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                            <Label className="text-xs uppercase">Date</Label>
                            <Input
                                type="date"
                                value={expenseDate}
                                onChange={(e) => setExpenseDate(e.target.value)}
                                data-testid="expense-date-input"
                            />
                            <p className="text-[10px] text-muted-foreground leading-tight">
                                Format: YYYY-MM-DD · defaults to today
                            </p>
                        </div>
                        <div className="space-y-1">
                            <Label className="text-xs uppercase">Payment</Label>
                            <select
                                value={paymentMode}
                                onChange={(e) => setPaymentMode(e.target.value)}
                                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                data-testid="expense-payment-select"
                            >
                                {PAYMENT_MODES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                            </select>
                        </div>
                    </div>

                    <div className="space-y-1">
                        <Label className="text-xs uppercase">Vendor / Payee (optional)</Label>
                        <Input
                            value={vendor}
                            onChange={(e) => setVendor(e.target.value)}
                            placeholder="e.g. Ram Sabziwala, Landlord, Metro"
                            data-testid="expense-vendor-input"
                        />
                    </div>

                    <div className="space-y-1">
                        <Label className="text-xs uppercase">Notes (optional)</Label>
                        <Input
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder="Any context worth saving"
                            data-testid="expense-notes-input"
                        />
                    </div>
                </div>

                <DialogFooter className="gap-2">
                    <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}>
                        Cancel
                    </Button>
                    <Button
                        variant="outline"
                        onClick={() => save(true)}
                        disabled={!canSave || saving}
                        data-testid="save-and-add-another-btn"
                    >
                        {saving && <Loader2 size={12} className="mr-1 animate-spin" />}
                        Save &amp; add another
                    </Button>
                    <Button
                        onClick={() => save(false)}
                        disabled={!canSave || saving}
                        data-testid="save-expense-btn"
                    >
                        {saving && <Loader2 size={12} className="mr-1 animate-spin" />}
                        Save
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

/* ---------- Recurring templates manager ---------- */

function RecurringManagerDialog({ open, onOpenChange, templates, categories, onCategoriesChange, onChanged, onRunNow, busy }) {
    const [showForm, setShowForm] = useState(false);
    const [editing, setEditing] = useState(null);

    const startCreate = () => { setEditing(null); setShowForm(true); };
    const startEdit = (t) => { setEditing(t); setShowForm(true); };

    return (
        <>
            <Dialog open={open} onOpenChange={onOpenChange}>
                <DialogContent data-testid="recurring-dialog" className="max-w-2xl max-h-[92vh] overflow-y-auto">
                    <DialogHeader>
                        <div className="flex items-center gap-2">
                            <div className="h-8 w-8 rounded-md bg-accent text-accent-foreground flex items-center justify-center">
                                <Repeat size={16} />
                            </div>
                            <DialogTitle className="font-heading">Recurring expenses</DialogTitle>
                        </div>
                        <DialogDescription>
                            Rent, wifi, cleaning fee — anything you pay on a schedule. Ease My Bill
                            auto-creates a <b>draft expense</b> on the due date. Confirm it in one tap when paid.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="flex items-center justify-between py-2">
                        <div className="text-xs text-muted-foreground">
                            {templates.length} template{templates.length !== 1 ? 's' : ''}
                        </div>
                        <div className="flex items-center gap-2">
                            <Button size="sm" variant="outline" onClick={onRunNow} disabled={busy} data-testid="run-drafter-btn">
                                <Play size={12} className="mr-1" /> Run now
                            </Button>
                            <Button size="sm" onClick={startCreate} data-testid="new-recurring-btn">
                                <Plus size={12} className="mr-1" /> New
                            </Button>
                        </div>
                    </div>

                    {templates.length === 0 ? (
                        <div className="text-sm text-muted-foreground italic py-8 text-center">
                            No recurring expenses yet. Add one for rent, wifi, or any monthly bill.
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {templates.map((t) => (
                                <RecurringRow
                                    key={t.id}
                                    t={t}
                                    onEdit={() => startEdit(t)}
                                    onChanged={onChanged}
                                />
                            ))}
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            <RecurringForm
                open={showForm}
                onOpenChange={setShowForm}
                editing={editing}
                categories={categories}
                onCategoriesChange={onCategoriesChange}
                onSaved={() => { setShowForm(false); onChanged?.(); }}
            />
        </>
    );
}

function RecurringRow({ t, onEdit, onChanged }) {
    const [busy, setBusy] = useState(false);
    const scheduleLabel = t.cadence === 'monthly'
        ? `${ordinal(t.day_of_month)} of every month`
        : `Every ${WEEKDAY_LABELS[t.day_of_week] || '?'}`;
    const toggleActive = async () => {
        setBusy(true);
        try {
            await api.updateRecurring(t.id, { active: !t.active });
            onChanged?.();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
        finally { setBusy(false); }
    };
    const del = async () => {
        if (!window.confirm(`Delete recurring "${t.label}"? Existing drafts stay.`)) return;
        setBusy(true);
        try {
            await api.deleteRecurring(t.id);
            toast.success('Deleted');
            onChanged?.();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
        finally { setBusy(false); }
    };
    return (
        <Card className={`p-3 flex items-center gap-3 ${!t.active ? 'opacity-60' : ''}`}>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium">{t.label}</span>
                    <Badge variant="outline" className="text-[10px]">{t.category_label}</Badge>
                    {!t.active && <Badge variant="outline" className="text-[10px]">Paused</Badge>}
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">
                    <b className="tabular-nums text-foreground">{fmtMoney(t.amount)}</b> · {scheduleLabel}
                    {t.vendor && ` · ${t.vendor}`}
                    {t.last_drafted_on && ` · last drafted ${fmtDate(t.last_drafted_on)}`}
                </div>
            </div>
            <div className="flex items-center gap-1">
                <Button size="sm" variant="ghost" onClick={toggleActive} disabled={busy}>
                    {t.active ? 'Pause' : 'Resume'}
                </Button>
                <Button size="sm" variant="ghost" onClick={onEdit} disabled={busy}>Edit</Button>
                <Button size="icon" variant="ghost" onClick={del} disabled={busy} aria-label="Delete">
                    <Trash2 size={14} />
                </Button>
            </div>
        </Card>
    );
}

function RecurringForm({ open, onOpenChange, editing, categories, onCategoriesChange, onSaved }) {
    const [label, setLabel] = useState('');
    const [amount, setAmount] = useState('');
    const [categoryKey, setCategoryKey] = useState('');
    const [vendor, setVendor] = useState('');
    const [cadence, setCadence] = useState('monthly');
    const [dayOfMonth, setDayOfMonth] = useState(1);
    const [dayOfWeek, setDayOfWeek] = useState(0);
    const [paymentMode, setPaymentMode] = useState('bank');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return;
        if (editing) {
            setLabel(editing.label || '');
            setAmount(String(editing.amount || ''));
            setCategoryKey(editing.category_key || '');
            setVendor(editing.vendor || '');
            setCadence(editing.cadence || 'monthly');
            setDayOfMonth(editing.day_of_month || 1);
            setDayOfWeek(editing.day_of_week ?? 0);
            setPaymentMode(editing.payment_mode || 'bank');
        } else {
            setLabel('');
            setAmount('');
            setCategoryKey('');
            setVendor('');
            setCadence('monthly');
            setDayOfMonth(1);
            setDayOfWeek(0);
            setPaymentMode('bank');
        }
    }, [open, editing]);

    const canSave = label.trim() && amount && Number(amount) > 0 && categoryKey;

    const save = async () => {
        setSaving(true);
        try {
            const body = {
                label: label.trim(),
                amount: Number(amount),
                category_key: categoryKey,
                vendor: vendor.trim() || undefined,
                cadence,
                day_of_month: cadence === 'monthly' ? Number(dayOfMonth) : undefined,
                day_of_week: cadence === 'weekly' ? Number(dayOfWeek) : undefined,
                payment_mode: paymentMode,
            };
            if (editing) {
                await api.updateRecurring(editing.id, body);
                toast.success('Updated');
            } else {
                await api.createRecurring(body);
                toast.success('Recurring template created');
            }
            onSaved?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid="recurring-form-dialog" className="max-w-lg">
                <DialogHeader>
                    <DialogTitle className="font-heading">
                        {editing ? 'Edit recurring expense' : 'New recurring expense'}
                    </DialogTitle>
                </DialogHeader>
                <div className="space-y-3 py-2">
                    <div className="space-y-1">
                        <Label className="text-xs uppercase">Name</Label>
                        <Input
                            value={label}
                            onChange={(e) => setLabel(e.target.value.slice(0, 120))}
                            placeholder="Shop rent, Wifi (Jio Fiber), Cleaning fee…"
                            data-testid="recurring-label-input"
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                            <Label className="text-xs uppercase">Amount (₹)</Label>
                            <Input
                                type="number"
                                value={amount}
                                onChange={(e) => setAmount(e.target.value)}
                                data-testid="recurring-amount-input"
                            />
                        </div>
                        <div className="space-y-1">
                            <Label className="text-xs uppercase">Category</Label>
                            <CategoryPickerWithAdd
                                value={categoryKey}
                                onChange={setCategoryKey}
                                categories={categories}
                                onCategoriesChange={onCategoriesChange}
                                testId="recurring-category-select"
                                placeholder="Select…"
                            />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                            <Label className="text-xs uppercase">Cadence</Label>
                            <select
                                value={cadence}
                                onChange={(e) => setCadence(e.target.value)}
                                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                data-testid="recurring-cadence-select"
                            >
                                {CADENCES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                            </select>
                        </div>
                        <div className="space-y-1">
                            <Label className="text-xs uppercase">
                                {cadence === 'monthly' ? 'Day of month' : 'Day of week'}
                            </Label>
                            {cadence === 'monthly' ? (
                                <Input
                                    type="number"
                                    min={1} max={31}
                                    value={dayOfMonth}
                                    onChange={(e) => setDayOfMonth(e.target.value)}
                                    data-testid="recurring-dom-input"
                                />
                            ) : (
                                <select
                                    value={dayOfWeek}
                                    onChange={(e) => setDayOfWeek(e.target.value)}
                                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                    data-testid="recurring-dow-select"
                                >
                                    {WEEKDAY_LABELS.map((w, i) => <option key={w} value={i}>{w}</option>)}
                                </select>
                            )}
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                            <Label className="text-xs uppercase">Vendor / Payee</Label>
                            <Input
                                value={vendor}
                                onChange={(e) => setVendor(e.target.value)}
                                placeholder="Landlord, Jio, etc."
                            />
                        </div>
                        <div className="space-y-1">
                            <Label className="text-xs uppercase">Default payment</Label>
                            <select
                                value={paymentMode}
                                onChange={(e) => setPaymentMode(e.target.value)}
                                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                            >
                                {PAYMENT_MODES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                            </select>
                        </div>
                    </div>
                </div>
                <DialogFooter className="gap-2">
                    <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
                    <Button onClick={save} disabled={!canSave || saving} data-testid="save-recurring-btn">
                        {saving && <Loader2 size={12} className="mr-1 animate-spin" />}
                        {editing ? 'Update' : 'Create'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function ordinal(n) {
    if (!n) return '?';
    const s = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
}
